import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi, errMsg, fmtIST } from "@/lib/useApi";
import { CircleCheck } from "lucide-react";

const PRICE = { monthly: "₹499", half_yearly: "₹2,699", annual: "₹5,599" };
const PERIOD = { monthly: "/month", half_yearly: "/6 months", annual: "/year" };

export default function SubscriptionPage() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/payments/me").then((r) => setMe(r.data));
  useEffect(() => { api.get("/payments/plans").then((r) => setPlans(r.data)); load(); /* eslint-disable-next-line */ }, [api]);

  useEffect(() => {
    const txn = params.get("txn");
    if (!txn) return;
    api.post(`/payments/${txn}/reconcile`).then((r) => {
      const st = r.data.status;
      if (st === "paid") toast.success("Payment verified — your subscription is active.");
      else toast.message(`Payment status: ${st.replace("_", " ")}. Access is granted only after server-side verification.`);
      load();
    }).catch((e) => toast.error(errMsg(e))).finally(() => setParams({}, { replace: true }));
    // eslint-disable-next-line
  }, []);

  const pay = async (plan_id) => {
    setBusy(plan_id);
    try {
      const { data } = await api.post("/payments/initiate", { plan_id });
      const f = document.createElement("form");
      f.method = "POST"; f.action = data.action;
      Object.entries(data.fields).forEach(([k, v]) => { const i = document.createElement("input"); i.type = "hidden"; i.name = k; i.value = v; f.appendChild(i); });
      document.body.appendChild(f); f.submit();
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };

  const sub = me?.subscription;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="subscription-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Subscription</h1>
        <p className="text-slate-600 mt-2 text-sm">Paid access is granted only after PayU confirms the payment server-to-server.</p>
      </div>
      {me && !me.payu.configured && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="payments-not-configured">
          <AlertTitle>Payments are not yet available</AlertTitle>
          <AlertDescription>The administrator has not yet connected PayU. Plans are shown for reference.</AlertDescription>
        </Alert>
      )}
      <Card className="p-6 border-slate-200" data-testid="subscription-status">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Current status</p>
        {sub && <p className="mt-2 text-slate-900"><Badge className="bg-emerald-600 hover:bg-emerald-600 mr-2">Active</Badge>{sub.plan_id.replace("_", " ")} · valid until {fmtIST(sub.expires_at)}</p>}
        {sub && <p className="text-sm text-slate-600 mt-2" data-testid="subscription-allowance">{sub.exams_used} of {sub.exam_allowance} exams used — {sub.exams_remaining} remaining this term. Unused exams do not carry over.</p>}
        {!sub && <p className="mt-2 text-slate-700">No active subscription.</p>}
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => (
          <Card key={p.id} className={`p-6 border-slate-200 relative ${p.id === "half_yearly" ? "ring-2 ring-blue-500" : ""}`} data-testid={`subscription-plan-${p.id}`}>
            {p.id === "half_yearly" && <Badge className="absolute -top-3 left-6 bg-blue-600 hover:bg-blue-600 text-white">Best value</Badge>}
            <h3 className="font-display font-semibold text-lg">{p.name.replace("JRFHITS ", "")}</h3>
            <div className="mt-4 flex items-baseline gap-1"><span className="font-display font-extrabold text-3xl">{PRICE[p.id]}</span><span className="text-sm text-slate-500">{PERIOD[p.id]}</span></div>
            <ul className="mt-5 space-y-2 text-sm text-slate-600">
              {[`${p.exams} qualifying exams over ${p.days} days`, "All modes incl. full-length mocks", "AI study plans", "Progress analytics"].map((t) => (
                <li key={t} className="flex gap-2"><CircleCheck className="h-4 w-4 text-emerald-600 mt-0.5" />{t}</li>
              ))}
            </ul>
            <Button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={!me?.payu.configured || busy} onClick={() => pay(p.id)} data-testid={`subscription-select-${p.id}`}>
              {busy === p.id ? "Redirecting to PayU…" : sub ? "Extend" : "Subscribe"}
            </Button>
          </Card>
        ))}
      </div>
      {me?.payments?.length > 0 && (
        <Card className="p-6 border-slate-200">
          <h2 className="font-display font-semibold">Payment history</h2>
          <Table>
            <TableHeader><TableRow><TableHead>Transaction</TableHead><TableHead>Plan</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
            <TableBody>
              {me.payments.map((p) => (
                <TableRow key={p.txnid}><TableCell className="font-mono text-xs">{p.txnid}</TableCell><TableCell>{p.plan_id}</TableCell><TableCell>₹{p.amount}</TableCell>
                  <TableCell><Badge variant="outline">{p.status}</Badge></TableCell><TableCell className="text-xs">{fmtIST(p.created_at)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
